import { z } from 'zod';
import {
  ClusterAnalysisSummary,
  ClusterMergeSuggestion,
  ClusterSnapshot,
} from './cluster-enrichment';
import { BooleanQuery, Id, IsoDate, PaginationQuery, paginated, SortOrder } from './common';
import { KeywordRow, MonthlySearch } from './keywords';

/** Contrats des clusters (`api.md` § Mots-clés et clusters, fiche T14). */

export const ClusterStage = z.enum(['formed', 'prefiltered', 'triaged', 'enriched', 'analyzed']);
export type ClusterStage = z.infer<typeof ClusterStage>;

export const EligibilityStatus = z.enum(['pending', 'excluded', 'ineligible', 'candidate']);
export type EligibilityStatus = z.infer<typeof EligibilityStatus>;

export const EligibilityReason = z.object({ rule: z.string(), detail: z.string() });
export type EligibilityReason = z.infer<typeof EligibilityReason>;

/** `clusters.metrics` tel que stocké (`scoring.md` § 1) ; `null` tant que la phase `metrics` n'a pas tourné. */
export const ClusterMetrics = z.object({
  volume_sum: z.number(),
  volume_max: z.number(),
  keyword_count: z.number().int(),
  cpc_weighted_usd: z.number().nullable(),
  bid_low_p50_usd: z.number().nullable(),
  bid_high_p50_usd: z.number().nullable(),
  competition_index_avg: z.number().nullable(),
  task_verb_ratio: z.number(),
  modifier_ratio: z.number(),
  question_ratio: z.number(),
  informational_ratio: z.number(),
  brand_ratio: z.number(),
  news_local_ratio: z.number(),
  trend_slope_12m: z.number().nullable(),
  seasonality_index: z.number().nullable(),
  history_months: z.number().int(),
  cv_monthly: z.number().nullable(),
  monthly_series: z.array(MonthlySearch),
  computed_at: z.string().optional(),
});
export type ClusterMetrics = z.infer<typeof ClusterMetrics>;

export const ClusterSort = z.enum(['priorityScore', 'triageScore', 'volumeSum', 'updatedAt']);
export type ClusterSort = z.infer<typeof ClusterSort>;

/** `GET /clusters` : `marketId` requis ; les clusters fusionnés dans un autre sont exclus. */
export const ClustersQuery = PaginationQuery.extend({
  marketId: Id,
  stage: ClusterStage.optional(),
  eligibility: EligibilityStatus.optional(),
  minPriority: z.coerce.number().min(0).max(100).optional(),
  hasOpportunity: BooleanQuery.optional(),
  /** Recherche `ILIKE` sur `label`. */
  q: z.string().trim().min(1).max(200).optional(),
  sort: ClusterSort.default('volumeSum'),
  order: SortOrder.default('desc'),
});
export type ClustersQuery = z.infer<typeof ClustersQuery>;

export const ClusterRow = z.object({
  id: Id,
  label: z.string(),
  stage: ClusterStage,
  eligibilityStatus: EligibilityStatus,
  eligibilityReasons: z.array(EligibilityReason),
  priorityScore: z.number().nullable(),
  triageScore: z.number().nullable(),
  keywordCount: z.number().int(),
  metrics: ClusterMetrics.nullable(),
  opportunityId: Id.nullable(),
  isHeterogeneous: z.boolean(),
  updatedAt: IsoDate,
});
export type ClusterRow = z.infer<typeof ClusterRow>;

export const ClustersResponse = paginated(ClusterRow);
export type ClustersResponse = z.infer<typeof ClustersResponse>;

/**
 * `GET /clusters/:id` : `keywords` = primaires du cluster (50 premiers par volume, variantes
 * repliées dans `variantCount`) ; `snapshots` (avec items classés), `analyses` (liste) et
 * `mergeSuggestions` viennent des phases `serp`, `triage`/`deep` et `supply` (T17–T20).
 */
export const ClusterDetail = ClusterRow.extend({
  marketId: z.string(),
  primaryKeyword: KeywordRow.nullable(),
  keywords: z.array(KeywordRow),
  /** Ids des mots-clés représentatifs (`cluster_keywords.is_representative`). */
  representatives: z.array(z.number().int()),
  /** `clusters.triage` (dernier item de triage) tel que stocké. */
  triage: z.record(z.string(), z.unknown()).nullable(),
  /** `clusters.supply` (`scoring.md` § 1) tel que stocké. */
  supply: z.record(z.string(), z.unknown()).nullable(),
  softFlags: z.array(z.string()),
  snapshots: z.array(ClusterSnapshot),
  analyses: z.array(ClusterAnalysisSummary),
  mergeSuggestions: z.array(ClusterMergeSuggestion),
});
export type ClusterDetail = z.infer<typeof ClusterDetail>;

export const CLUSTER_DETAIL_KEYWORDS = 50;
